import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useRegistration } from '../../../contexts/RegistrationContext';

const StepContainer = styled.div`
  max-width: 700px;
  margin: 0 auto;
`;

const Title = styled.h2`
  color: #000;
  margin-bottom: 1rem;
  font-weight: 600;
`;

const Description = styled.p`
  color: #666;
  margin-bottom: 2rem;
`;

const SummarySection = styled.div`
  border: 1px solid #ddd;
  border-radius: 15px;
  margin-bottom: 1.5rem;
  overflow: hidden;
`;

const SectionHeader = styled.div`
  background: #f8f9fa;
  padding: 1rem;
  border-bottom: 1px solid #ddd;
  font-weight: 600;
  color: #000;
`;

const SectionContent = styled.div`
  padding: 1rem;
`;

const DataRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f0f0f0;
  
  &:last-child {
    border-bottom: none;
  }
`;

const DataLabel = styled.div`
  font-weight: 500;
  color: #000;
  min-width: 150px;
`;

const DataValue = styled.div`
  flex: 1;
  text-align: right;
  color: #000;
`;

const EditButton = styled.button`
  background: none;
  border: none;
  color: #000;
  cursor: pointer;
  font-size: 0.9rem;
  margin-left: 1rem;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    text-decoration: underline;
    color: #333;
  }
`;

const ConfirmationBox = styled.div`
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 15px;
  padding: 1.5rem;
  margin: 2rem 0;
  color: #856404;
`;

const CheckboxGroup = styled.div`
  margin: 1rem 0;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  margin-bottom: 0.5rem;
  color: #000;
  font-weight: 500;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 1rem;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 25px;
  font-size: 1.1rem;
  font-weight: 500;
  cursor: pointer;
  margin-top: 1rem;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: #218838;
    transform: translateY(-1px);
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const BackButton = styled.button`
  background: #6c757d;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  cursor: pointer;
  margin-bottom: 1rem;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: #5a6268;
    transform: translateY(-1px);
  }
`;

const Summary: React.FC = () => {
  const { registrationData, setCurrentStep, submitRegistration } = useRegistration();
  const [confirmed, setConfirmed] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const parseServices = () => {
    try {
      return registrationData.additional_services 
        ? JSON.parse(registrationData.additional_services)
        : {};
    } catch {
      return {};
    }
  };

  const parseOwners = () => {
    try {
      return registrationData.owners_data 
        ? JSON.parse(registrationData.owners_data)
        : [];
    } catch {
      return [];
    }
  };

  const parsePreferences = () => {
    try {
      return registrationData.charter_preferences 
        ? JSON.parse(registrationData.charter_preferences)
        : {};
    } catch {
      return {};
    }
  };

  const handleSubmit = async () => {
    if (!confirmed || !agreedToTerms) {
      alert('Please confirm all required checkboxes');
      return;
    }

    setIsSubmitting(true);
    try {
      await submitRegistration();
      alert('Registration submitted successfully! You will receive validation and invoice within 1 business day.');
      // Redirect to My Requests page after successful submission
      navigate('/my-requests');
    } catch (error) {
      console.error('Failed to submit registration:', error);
      alert('Failed to submit registration. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    setCurrentStep(5);
  };

  const services = parseServices();
  const owners = parseOwners();
  const preferences = parsePreferences();

  const selectedServices = Object.entries(services)
    .filter(([_, selected]) => selected)
    .map(([key, _]) => key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));

  const selectedPreferences = Object.entries(preferences)
    .filter(([_, selected]) => selected)
    .map(([key, _]) => key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));

  return (
    <StepContainer>
              <Title>Step 6: Review & Submit Registration</Title>
      <Description>
        Please review all information before submitting your company registration application.
      </Description>

      <BackButton onClick={handleBack}>← Back: Additional Services</BackButton>

      <SummarySection>
        <SectionHeader>
          Business Activity
          <EditButton onClick={() => setCurrentStep(1)}>Edit</EditButton>
        </SectionHeader>
        <SectionContent>
          <DataRow>
            <DataLabel>Activity Code:</DataLabel>
            <DataValue>{registrationData.business_activity_code}</DataValue>
          </DataRow>
          <DataRow>
            <DataLabel>Activity:</DataLabel>
            <DataValue>{registrationData.business_activity}</DataValue>
          </DataRow>
          <DataRow>
            <DataLabel>Description:</DataLabel>
            <DataValue>{registrationData.business_description}</DataValue>
          </DataRow>
        </SectionContent>
      </SummarySection>

      <SummarySection>
        <SectionHeader>
          Company Name
          <EditButton onClick={() => setCurrentStep(2)}>Edit</EditButton>
        </SectionHeader>
        <SectionContent>
          <DataRow>
            <DataLabel>Armenian:</DataLabel>
            <DataValue>{registrationData.company_name_armenian}</DataValue>
          </DataRow>
          {registrationData.company_name_english && (
            <DataRow>
              <DataLabel>English:</DataLabel>
              <DataValue>{registrationData.company_name_english}</DataValue>
            </DataRow>
          )}
          {registrationData.company_name_russian && (
            <DataRow>
              <DataLabel>Russian:</DataLabel>
              <DataValue>{registrationData.company_name_russian}</DataValue>
            </DataRow>
          )}
        </SectionContent>
      </SummarySection>

      <SummarySection>
        <SectionHeader>
          Owners Information and Share Distribution
          <EditButton onClick={() => setCurrentStep(3)}>Edit</EditButton>
        </SectionHeader>
        <SectionContent>
          <DataRow>
            <DataLabel>Authorized Capital:</DataLabel>
            <DataValue>{registrationData.authorized_capital?.toLocaleString()} AMD</DataValue>
          </DataRow>
          <DataRow>
            <DataLabel>Owners:</DataLabel>
            <DataValue>
              {owners.map((owner: any, index: number) => (
                <div key={index} style={{ marginBottom: '0.5rem' }}>
                  <strong>{owner.full_name}</strong> ({owner.share_percentage}%)
                  <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.25rem' }}>
                    Address: {owner.address}
                  </div>
                  <div style={{ fontSize: '0.9rem', color: '#666' }}>
                    Passport: {owner.passport_scan_path ? '✓ Uploaded' : 'Not uploaded'}
                    {owner.additional_proof_path && ' | Additional Proof: ✓ Uploaded'}
                  </div>
                </div>
              ))}
            </DataValue>
          </DataRow>
        </SectionContent>
      </SummarySection>

      <SummarySection>
        <SectionHeader>
          Charter Details
          <EditButton onClick={() => setCurrentStep(4)}>Edit</EditButton>
        </SectionHeader>
        <SectionContent>
          <DataRow>
            <DataLabel>Decision Making:</DataLabel>
            <DataValue>{registrationData.decision_making?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</DataValue>
          </DataRow>
          {selectedPreferences.length > 0 && (
            <DataRow>
              <DataLabel>Preferences:</DataLabel>
              <DataValue>
                {selectedPreferences.map((pref, index) => (
                  <div key={index}>• {pref}</div>
                ))}
              </DataValue>
            </DataRow>
          )}
        </SectionContent>
      </SummarySection>

      <SummarySection>
        <SectionHeader>
          Additional Services
          <EditButton onClick={() => setCurrentStep(5)}>Edit</EditButton>
        </SectionHeader>
        <SectionContent>
          <DataRow>
            <DataLabel>Selected Services:</DataLabel>
            <DataValue>
              {selectedServices.length > 0 ? (
                selectedServices.map((service, index) => (
                  <div key={index}>• {service}</div>
                ))
              ) : (
                'None selected'
              )}
            </DataValue>
          </DataRow>
          <DataRow>
            <DataLabel>Communication:</DataLabel>
            <DataValue>{registrationData.preferred_communication?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</DataValue>
          </DataRow>
          {registrationData.additional_comments && (
            <DataRow>
              <DataLabel>Comments:</DataLabel>
              <DataValue>{registrationData.additional_comments}</DataValue>
            </DataRow>
          )}
        </SectionContent>
      </SummarySection>

      <ConfirmationBox>
        <h4>Final Confirmation and Submission</h4>
        <p>
          By submitting this registration, you confirm that:
        </p>
        <CheckboxGroup>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
            />
            All information provided is accurate and complete
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
            />
            I agree to the terms and conditions of service
          </CheckboxLabel>
        </CheckboxGroup>
        <p>
          <strong>Next Steps:</strong>
        </p>
        <ul>
          <li>Information will be validated within 1 business day</li>
          <li>Invoice will be prepared and sent to you</li>
          <li>Your assigned manager will contact you via your preferred communication method</li>
          <li>Status will be updated to "Awaiting Payment"</li>
        </ul>
      </ConfirmationBox>

      <SubmitButton
        onClick={handleSubmit}
        disabled={!confirmed || !agreedToTerms || isSubmitting}
      >
        {isSubmitting ? 'Submitting Registration...' : 'Submit Company Registration'}
      </SubmitButton>
    </StepContainer>
  );
};

export default Summary;